import { type ConversationMessageApiModel as ChatMessage, FEEDBACK_VALUES, type FeedbackValue } from "@noa/api-core";
import { parseMessageWithReferences, useMessageFeedback } from "@noa/api-client";
import { message } from "~/components/ui";
import { type FunctionComponent, type CSSProperties } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { useTranslation } from "react-i18next";
import { Clipboard, ThumbsDown, ThumbsUp } from "lucide-react";

import { useOnClipboardCopy } from "~/hooks/use-on-clipboard-copy";
import { useSessionId, useSetMessageFeedback } from "@noa/client-store";
import { markdownToHtml } from "~/utils/markdown.utils";

interface ChatMessageToolsProps {
  chatMessage: ChatMessage;
}

interface SendFeedbackArgs {
  feedbackAction: FeedbackValue;
  chatMessage: ChatMessage;
}

export const ChatMessageTools: FunctionComponent<ChatMessageToolsProps> = ({ chatMessage }) => {
  const { t } = useTranslation();
  const onCopyToClipboard = useOnClipboardCopy();
  const sessionId = useSessionId();
  const messageId = chatMessage.message_id;

  const { sendMessageFeedback } = useMessageFeedback({ sessionId, messageId });
  const setMessageFeedback = useSetMessageFeedback();

  const { content } = parseMessageWithReferences(chatMessage.content);

  const iconSize = 16;
  const iconStyle: CSSProperties = { background: "none", border: "none", padding: 0, cursor: "pointer" };
  const defaultColor = "var(--text-muted)";
  const thumbsUpColor = chatMessage.feedback === FEEDBACK_VALUES.POSITIVE ? "var(--info)" : defaultColor;
  const thumbsDownColor = chatMessage.feedback === FEEDBACK_VALUES.NEGATIVE ? "var(--danger)" : defaultColor;

  const handleSendMessageFeedback = async ({ feedbackAction, chatMessage }: SendFeedbackArgs) => {
    const wasAlreadyProvided = chatMessage.feedback === feedbackAction;
    const feedback = wasAlreadyProvided ? 0 : feedbackAction;
    const feedbackMessage = wasAlreadyProvided ? t("common:feedbackCancelled") : t("common:feedbackSent");

    try {
      await sendMessageFeedback({ sessionId, messageId, feedback });
      setMessageFeedback(messageId, feedback);
      message.info(feedbackMessage);
    } catch (e: unknown) {
      console.error("[handleSendMessageFeedback] Error:", e);
      message.error(t("common:errorUnexpected"));
    }
  };

  return (
    <div style={{ marginLeft: "36px", marginTop: "4px", gap: "12px", display: "flex", alignItems: "center" }}>
      <button
        aria-label={t("common:feedbackPositive")}
        title={t("common:feedbackPositive")}
        style={iconStyle}
        onClick={() => handleSendMessageFeedback({ feedbackAction: FEEDBACK_VALUES.POSITIVE, chatMessage })}
      >
        <ThumbsUp size={iconSize} color={thumbsUpColor} />
      </button>
      <button
        aria-label={t("common:feedbackNegative")}
        title={t("common:feedbackNegative")}
        style={iconStyle}
        onClick={() => handleSendMessageFeedback({ feedbackAction: FEEDBACK_VALUES.NEGATIVE, chatMessage })}
      >
        <ThumbsDown size={iconSize} color={thumbsDownColor} />
      </button>

      <CopyToClipboard options={{ format: "text/html" }} text={markdownToHtml(content)} onCopy={onCopyToClipboard}>
        <button title={t("common:copy")} aria-label={t("common:copy")} style={iconStyle}>
          <Clipboard size={iconSize} className="text-noa-muted" />
        </button>
      </CopyToClipboard>
    </div>
  );
};
